import SummaryReportsAPI from 'dashboard/api/summaryReports';
import camelcaseKeys from 'camelcase-keys';

const typeMap = {
  inbox: {
    flagKey: 'isFetchingInboxSummaryReports',
    apiMethod: 'getInboxReports',
    mutationKey: 'setInboxSummaryReport',
  },
  agent: {
    flagKey: 'isFetchingAgentSummaryReports',
    apiMethod: 'getAgentReports',
    mutationKey: 'setAgentSummaryReport',
  },
  team: {
    flagKey: 'isFetchingTeamSummaryReports',
    apiMethod: 'getTeamReports',
    mutationKey: 'setTeamSummaryReport',
  },
  label: {
    flagKey: 'isFetchingLabelSummaryReports',
    apiMethod: 'getLabelReports',
    mutationKey: 'setLabelSummaryReport',
  },
};

const isCurrentRequest = (state, rootGetters, request) =>
  state.requestIds[request.type] === request.id &&
  rootGetters?.getCurrentAccountId === request.accountId;

async function fetchSummaryReports(type, params, context) {
  const { commit, state, rootGetters } = context;
  const config = typeMap[type];
  if (!config) return;

  const accountId = rootGetters?.getCurrentAccountId;
  commit('startRequest', type);
  const request = { accountId, id: state.requestIds[type], type };

  let error = null;
  try {
    commit('setUIFlags', { [config.flagKey]: true });
    const response = await SummaryReportsAPI[config.apiMethod](params);
    if (isCurrentRequest(state, rootGetters, request)) {
      commit(config.mutationKey, camelcaseKeys(response.data, { deep: true }));
    }
  } catch (e) {
    error = e;
  } finally {
    if (isCurrentRequest(state, rootGetters, request)) {
      commit('setUIFlags', { [config.flagKey]: false });
    }
  }
  if (error) throw error;
}

export const initialState = {
  requestIds: {},
  inboxSummaryReports: [],
  agentSummaryReports: [],
  teamSummaryReports: [],
  labelSummaryReports: [],
  uiFlags: {
    isFetchingInboxSummaryReports: false,
    isFetchingAgentSummaryReports: false,
    isFetchingTeamSummaryReports: false,
    isFetchingLabelSummaryReports: false,
  },
};

export const getters = {
  getInboxSummaryReports(state) {
    return state.inboxSummaryReports;
  },
  getAgentSummaryReports(state) {
    return state.agentSummaryReports;
  },
  getTeamSummaryReports(state) {
    return state.teamSummaryReports;
  },
  getLabelSummaryReports(state) {
    return state.labelSummaryReports;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  fetchInboxSummaryReports(context, params) {
    return fetchSummaryReports('inbox', params, context);
  },

  fetchAgentSummaryReports(context, params) {
    return fetchSummaryReports('agent', params, context);
  },

  fetchTeamSummaryReports(context, params) {
    return fetchSummaryReports('team', params, context);
  },

  fetchLabelSummaryReports(context, params) {
    return fetchSummaryReports('label', params, context);
  },
};

export const mutations = {
  startRequest(state, type) {
    state.requestIds[type] = (state.requestIds[type] || 0) + 1;
  },
  setInboxSummaryReport(state, data) {
    state.inboxSummaryReports = data;
  },
  setAgentSummaryReport(state, data) {
    state.agentSummaryReports = data;
  },
  setTeamSummaryReport(state, data) {
    state.teamSummaryReports = data;
  },
  setLabelSummaryReport(state, data) {
    state.labelSummaryReports = data;
  },
  setUIFlags(state, uiFlag) {
    state.uiFlags = { ...state.uiFlags, ...uiFlag };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
